import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  MapPin,
  Users,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Tag,
  Filter,
  Search,
  Eye, // Changed from Download
  Trash2
} from 'lucide-react';

function BookingHistory() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cancelingId, setCancelingId] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      // If no user, redirect to login
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (user?.email) {
      fetchBookings();
    }
  }, [user]);

  useEffect(() => {
    let filtered = bookings;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(b => b.status === filterStatus);
    }

    if (searchQuery) {
      filtered = filtered.filter(b =>
        b.bookingReference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.tour?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredBookings(filtered);
  }, [bookings, filterStatus, searchQuery]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://api.boomontrip.com/api/bookings/email/${user.email}`
      );

      const data = await response.json();

      if (data.success) {
        const sortedBookings = data.data.sort((a, b) =>
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setBookings(sortedBookings);
        setFilteredBookings(sortedBookings);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      alert('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId, bookingRef) => {
    console.log('🔍 Cancel booking initiated:', { bookingId, bookingRef });

    const confirmed = window.confirm(
      `Are you sure you want to cancel booking ${bookingRef}?\n\nThis action cannot be undone.`
    );

    if (!confirmed) {
      console.log('❌ User cancelled the cancellation');
      return;
    }

    try {
      setCancelingId(bookingId);
      console.log('📡 Method 1: Trying PATCH /api/bookings/:id/cancel...');

      // Method 1: Try dedicated cancel endpoint
      let response = await fetch(
        `https://api.boomontrip.com/api/bookings/${bookingId}/cancel`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: user?.email
          })
        }
      );

      console.log('📥 Method 1 Response status:', response.status);

      // Method 2: If 404, try status update endpoint
      if (response.status === 404) {
        console.log('⚠️ Method 1 failed (404), trying Method 2: PATCH /api/bookings/:id/status...');

        response = await fetch(
          `https://api.boomontrip.com/api/bookings/${bookingId}/status`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              status: 'cancelled'
            })
          }
        );

        console.log('📥 Method 2 Response status:', response.status);
      }

      // Method 3: If still failing, try PUT with full update
      if (response.status === 404) {
        console.log('⚠️ Method 2 failed (404), trying Method 3: PUT /api/bookings/:id...');

        response = await fetch(
          `https://api.boomontrip.com/api/bookings/${bookingId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              status: 'cancelled'
            })
          }
        );

        console.log('📥 Method 3 Response status:', response.status);
      }

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error response:', errorText);
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }

      // Parse response
      let data;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        console.log('📦 Success response data:', data);
      } else {
        const text = await response.text();
        console.log('📦 Success response text:', text);
        data = { success: true };
      }

      // Check success
      if (data.success || response.status === 200) {
        console.log('✅ Booking cancelled successfully!');
        alert('✅ Booking cancelled successfully!');

        // Update local state immediately
        setBookings(prev =>
          prev.map(b =>
            b._id === bookingId
              ? { ...b, status: 'cancelled' }
              : b
          )
        );

        // Refresh from server
        await fetchBookings();
      } else {
        throw new Error(data.message || 'Failed to cancel booking');
      }

    } catch (error) {
      console.error('❌ Cancel booking error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });

      alert(
        `❌ Failed to cancel booking\n\n` +
        `Error: ${error.message}\n\n` +
        `Please try again or contact support.\n` +
        `Booking Reference: ${bookingRef}`
      );
    } finally {
      setCancelingId(null);
    }
  };

  // ✅ FIXED: View details - opens modal or shows alert
  const handleViewDetails = (booking) => {
    console.log('🔍 View details clicked:', booking);

    // Option 1: Show alert with details (temporary solution)
    const details = `
📋 Booking Details
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Reference: ${booking.bookingReference}
Tour: ${booking.tour || 'N/A'}
Status: ${booking.status.toUpperCase()}

📅 Tour Date: ${new Date(booking.date).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}

👤 Contact: ${booking.name}
📧 Email: ${booking.email}
📱 Phone: ${booking.phone}

👥 Travelers: ${booking.adults} Adult(s)${booking.children > 0 ? `, ${booking.children} Child(ren)` : ''}
📍 Pickup: ${booking.pickup}

💰 Total Amount: ₹${booking.totalPrice.toLocaleString('en-IN')}
${booking.discount ? `💸 Discount: ${booking.discount.percent}% OFF (Saved ₹${booking.discount.amount})` : ''}

🕐 Booked On: ${new Date(booking.createdAt).toLocaleString('en-IN')}
    `.trim();

    alert(details);

    // Option 2: Navigate to dedicated details page (if you create one)
    // navigate(`/booking-details/${booking._id}`);

    // Option 3: Open modal (implement modal component)
    // setSelectedBooking(booking);
    // setShowDetailsModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: Clock,
        label: 'Pending'
      },
      confirmed: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircle,
        label: 'Confirmed'
      },
      completed: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        icon: CheckCircle,
        label: 'Completed'
      },
      cancelled: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: XCircle,
        label: 'Cancelled'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getStats = () => {
    return {
      total: bookings.length,
      pending: bookings.filter(b => b.status === 'pending').length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      completed: bookings.filter(b => b.status === 'completed').length,
      cancelled: bookings.filter(b => b.status === 'cancelled').length,
    };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">My Bookings</h1>
          <p className="text-gray-600">View and manage all your tour bookings</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-2xl font-bold text-gray-800">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Bookings</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-2xl font-bold text-green-600">{stats.confirmed}</div>
            <div className="text-sm text-gray-600">Confirmed</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.completed}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
            <div className="text-sm text-gray-600">Cancelled</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by reference, name, or tour..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div className="md:w-64">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none appearance-none bg-white"
                >
                  <option value="all">All Bookings</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bookings List */}
        {filteredBookings.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Bookings Found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || filterStatus !== 'all'
                ? 'Try adjusting your filters or search query'
                : 'You haven\'t made any bookings yet'}
            </p>
            {!searchQuery && filterStatus === 'all' && (
              <button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Browse Tours
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <div key={booking._id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-800">
                          {booking.tour || 'Tour Booking'}
                        </h3>
                        {getStatusBadge(booking.status)}
                      </div>
                      <p className="text-sm text-gray-600">
                        Reference: <span className="font-semibold text-orange-600">{booking.bookingReference}</span>
                      </p>
                    </div>
                    <div className="text-right">
                      {booking.discount && booking.originalPrice && (
                        <div className="text-sm text-gray-400 line-through">
                          ₹{booking.originalPrice.toLocaleString('en-IN')}
                        </div>
                      )}
                      <div className="text-2xl font-bold text-orange-600">
                        ₹{booking.totalPrice.toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Tour Date</p>
                        <p className="font-semibold text-gray-800">
                          {new Date(booking.date).toLocaleDateString('en-IN', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Travelers</p>
                        <p className="font-semibold text-gray-800">
                          {booking.adults} Adult{booking.adults > 1 ? 's' : ''}
                          {booking.children > 0 && `, ${booking.children} Child${booking.children > 1 ? 'ren' : ''}`}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Pickup Point</p>
                        <p className="font-semibold text-gray-800 text-sm">{booking.pickup}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Email</p>
                        <p className="font-semibold text-gray-800 text-sm">{booking.email}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Phone</p>
                        <p className="font-semibold text-gray-800">{booking.phone}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-orange-600 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600">Booked On</p>
                        <p className="font-semibold text-gray-800">
                          {new Date(booking.createdAt).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Discount Info */}
                  {booking.discount && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-green-800">
                        <Tag className="w-4 h-4" />
                        <span className="text-sm font-semibold">
                          {booking.discount.type === 'couple' ? 'Couple Discount' :
                            booking.discount.type === 'family_first' ? 'Family First Discount' :
                              'First Booking Discount'} - {booking.discount.percent}% OFF
                        </span>
                        <span className="ml-auto font-bold">
                          Saved ₹{Math.round(booking.discount.amount).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t">
                    <button
                      onClick={() => handleViewDetails(booking)}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-semibold"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>

                    {(booking.status === 'pending' || booking.status === 'confirmed') && (
                      <button
                        onClick={() => handleCancelBooking(booking._id, booking.bookingReference)}
                        disabled={cancelingId === booking._id}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {cancelingId === booking._id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700"></div>
                            Cancelling...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            Cancel Booking
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingHistory;